import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { DragDropContext, Draggable, DraggableProvidedDragHandleProps, Droppable } from 'react-beautiful-dnd'
// hooks
import { useShowModal } from 'hooks/useShowModal'
// components
import AddFieldButton from '../AddFieldButton'
import ControlButtons from '../ControlButtons'
import RiskTemplateField from '../RiskTemplateField'
// types
import { IField, IFieldUpdatePositionInput } from 'types/graphql'
// constants
import { Modals } from 'constants/modals'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// themes
import { colors } from 'themes/index'
// views
import { SectionWrapper, SectionName, Content, Collapse } from './views'

interface Props {
  field: IField
  fields: IField[]
  dragHandleProps?: DraggableProvidedDragHandleProps
}

const Section: React.FC<Props> = ({ field, fields, dragHandleProps }) => {
  const router = useRouter()

  const { isRiskTemplate, deleteField, reorderFields } = React.useContext(RiskTemplateAPIContext)

  const { showModal } = useShowModal({ modalType: Modals.CONFIRM_DELETE_RISK_MODAL })

  const [isCollapsed, setExpanded] = React.useState(false)

  const onCollapseClick = () => setExpanded(!isCollapsed)

  const onDeleteClick = () => {
    showModal({
      width: 'sm',
      entity: 'field',
      handleConfirm: () => deleteField(field)
    })
  }

  const reorder = (list: IField[], startIdx: number, endIdx: number) => {
    const fieldsOrderInput: IFieldUpdatePositionInput[] = []

    const [removed] = list.splice(startIdx, 1)

    list.splice(endIdx, 0, removed)

    list.forEach((field, idx) => {
      if (field.position !== idx) {
        fieldsOrderInput.push({
          id: field.id,
          position: idx
        })
      }
    })

    return fieldsOrderInput
  }

  const onDragEnd = ({ source, destination }) => {
    if (!destination) {
      return
    }

    if (source.droppableId === destination.droppableId) {
      const fieldsOrderInput = reorder(fields, source.index, destination.index)

      if (fieldsOrderInput.length) {
        reorderFields(fieldsOrderInput)
      }
    }
  }

  const bgColor = field.backgroundColor || colors.gray190

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <SectionWrapper $color={bgColor}>
        <Content $isCollapsed={isCollapsed} $color={bgColor} $height={isCollapsed ? 54 : fields.length * 80 + 60}>
          <Droppable droppableId={`SECTION_FIELDS_${field.id}`}>
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {isRiskTemplate && <ControlButtons field={field} onDeleteClick={onDeleteClick} />}
                <Collapse
                  $color={bgColor}
                  disabled={!fields.length}
                  isCollapsed={isCollapsed}
                  handleClick={onCollapseClick}>
                  <SectionName $color={bgColor} $disabled={!fields.length} {...dragHandleProps}>
                    {field.label}
                  </SectionName>
                </Collapse>
                {!isCollapsed && (
                  <>
                    {fields.map((field, idx) => (
                      <Draggable
                        index={idx}
                        key={field.id}
                        draggableId={field.id}
                        isDragDisabled={!isRiskTemplate || !!router.query.archived}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={provided.draggableProps.style as React.CSSProperties}>
                            <RiskTemplateField dragHandleProps={provided.dragHandleProps} field={field} />
                          </div>
                        )}
                      </Draggable>
                    ))}
                  </>
                )}
              </div>
            )}
          </Droppable>
        </Content>

        {isRiskTemplate && <AddFieldButton parentField={field} label='Add item to section' />}
      </SectionWrapper>
    </DragDropContext>
  )
}

export default Section
